import { Box, CircularProgress, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material'
import { Settings } from '@mui/icons-material'
import type { CRMObjectRow } from '../../api/client'
import { useAuthStore } from '../../stores/auth.store'
import { getIconComponent } from '../../routes/settings/ObjectManagement'

const DRAWER_WIDTH = 240

interface ObjectSidebarProps {
  open: boolean
  pathname: string
  objects: CRMObjectRow[]
  loading: boolean
  onNavigate: (path: string) => void
}

export function ObjectSidebar({ open, pathname, objects, loading, onNavigate }: ObjectSidebarProps) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.is_tenant_admin

  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ minHeight: 48 }} />
      <Box sx={{ overflow: 'auto', pt: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
          Objects
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : objects.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            No objects available
          </Typography>
        ) : (
          <List dense>
            {objects.map((objectItem) => {
              const IconComp = getIconComponent(objectItem.icon)
              return (
                <ListItemButton
                  key={objectItem.id}
                  selected={pathname.startsWith(`/o/${objectItem.id}`)}
                  onClick={() => onNavigate(`/o/${objectItem.id}`)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <IconComp fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={objectItem.label_plural || objectItem.label} />
                </ListItemButton>
              )
            })}
          </List>
        )}
        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
              Admin
            </Typography>
            <List dense>
              <ListItemButton
                selected={pathname.startsWith('/settings')}
                onClick={() => onNavigate('/settings/system')}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </List>
          </>
        )}
      </Box>
    </Drawer>
  )
}
